import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Activity, ShieldCheck, Send, Lock, Timer, Coffee, Code, Send as SendIcon,
  MessageSquare, FileText, Paintbrush, Compass, Calendar, ChevronLeft, ChevronRight, Loader2,
} from "lucide-react";

const CATEGORY_META = {
  coding:        { icon: Code, color: "bg-emerald-500", label: "Coding" },
  deployment:    { icon: SendIcon, color: "bg-sky-500", label: "Deployment" },
  communication: { icon: MessageSquare, color: "bg-fuchsia-500", label: "Comms" },
  documentation: { icon: FileText, color: "bg-amber-500", label: "Docs" },
  design:        { icon: Paintbrush, color: "bg-pink-500", label: "Design" },
  browsing:      { icon: Compass, color: "bg-slate-500", label: "Browsing" },
  idle:          { icon: Coffee, color: "bg-zinc-500", label: "Idle" },
};

function fmtMinutes(m) {
  m = Math.max(0, Math.round(m || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r}m`;
  if (r === 0) return `${h}h`;
  return `${h}h ${r}m`;
}

function todayISO(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

export default function WorkLog() {
  const [status, setStatus] = useState(null);
  const [date, setDate] = useState(todayISO(0));
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selfReport, setSelfReport] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [acking, setAcking] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/work-intelligence/status");
        if (alive) setStatus(r.data);
      } catch (e) {
        if (alive) setStatus({ enabled: false });
      }
    })();
    return () => { alive = false; };
  }, []);

  const load = async (d) => {
    setLoading(true);
    try {
      const r = await api.get("/work-intelligence/my", { params: { date: d } });
      setSession(r.data);
      setSelfReport(r.data?.self_report || "");
    } catch (e) {
      setSession(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (status?.enabled) load(date); }, [date, status?.enabled]);

  const totalMinutes = useMemo(() => {
    if (!session?.categories) return 0;
    return Object.values(session.categories).reduce((s, v) => s + (v || 0), 0);
  }, [session]);

  const submitted = !!session?.submitted_at;
  const empty = !session || session.empty || totalMinutes === 0;

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.patch("/work-intelligence/self-report", {
        date, self_report: selfReport, submit: false,
      });
      toast.success("Draft saved");
      await load(date);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const submitDay = async () => {
    if (!selfReport.trim()) return toast.error("Add a brief self-report before submitting");
    if (!confirm("Once submitted, this day is locked and visible to your manager. Continue?")) return;
    setSubmitting(true);
    try {
      await api.patch("/work-intelligence/self-report", {
        date, self_report: selfReport, submit: true,
      });
      toast.success("Submitted for manager review");
      await load(date);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const acknowledge = async () => {
    setAcking(true);
    try {
      await api.post("/work-intelligence/acknowledge", {});
      const r = await api.get("/work-intelligence/status");
      setStatus(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setAcking(false); }
  };

  if (!status) return <div className="p-8 text-muted-foreground">Loading…</div>;

  if (!status.enabled) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="wi-disabled-panel">
        <div className="border border-border/60 rounded-lg p-8 bg-secondary/20">
          <Timer className="w-8 h-8 text-muted-foreground mb-3" />
          <h1 className="text-2xl font-display">Work Intelligence is not enabled</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Your organisation hasn't activated the Work Intelligence add-on. Ask an admin to activate it under Settings if you need daily work summaries.
          </p>
        </div>
      </div>
    );
  }

  if (!status.user_acknowledged) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="wi-ack-panel">
        <div className="border border-border/60 rounded-lg p-8 bg-secondary/20">
          <ShieldCheck className="w-8 h-8 text-sky-500 mb-3" />
          <h1 className="text-2xl font-display">Acknowledge Work Intelligence</h1>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
            Your work activity categories (coding, meetings, docs, etc — never URLs, titles or keystrokes) are passively captured during working hours.
            You'll always see and submit your own daily summary before your manager can view it.
            You retain the right to add context in the self-report field.
          </p>
          <Button
            data-testid="wi-ack-btn"
            className="mt-6"
            onClick={acknowledge}
            disabled={acking}
          >
            {acking && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            I understand — start capture
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="worklog-page">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-sky-500">Work log</p>
          <h1 className="font-display text-3xl mt-1">Your daily summary</h1>
          <p className="text-sm text-muted-foreground mt-1">
            You see this before your manager does. Review, add context, and submit when the day is complete.
          </p>
        </div>
        <div className="flex items-center gap-2 border border-border/60 rounded-lg px-3 py-2 bg-secondary/40">
          <button
            data-testid="worklog-prev-day"
            className="p-1 text-muted-foreground hover:text-foreground"
            onClick={() => setDate((d) => {
              const nd = new Date(d); nd.setDate(nd.getDate() - 1); return nd.toISOString().slice(0, 10);
            })}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <input
            type="date"
            data-testid="worklog-date-input"
            value={date}
            max={todayISO(0)}
            onChange={(e) => setDate(e.target.value)}
            className="bg-transparent text-sm outline-none w-[140px]"
          />
          <button
            data-testid="worklog-next-day"
            className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-40"
            disabled={date >= todayISO(0)}
            onClick={() => setDate((d) => {
              const nd = new Date(d); nd.setDate(nd.getDate() + 1);
              return nd.toISOString().slice(0, 10);
            })}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </header>

      {loading && <p className="text-muted-foreground text-sm">Loading day…</p>}

      {!loading && empty && (
        <div className="border border-dashed border-border/60 rounded-lg p-10 text-center text-muted-foreground" data-testid="worklog-empty">
          No activity captured for this day yet. The desktop agent hasn't ingested any events, or you weren't working.
        </div>
      )}

      {!loading && !empty && (
        <>
          <div className="border border-border/60 rounded-lg p-6 bg-secondary/10">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Total captured</p>
              <span
                data-testid="worklog-status-chip"
                className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded ${
                  submitted
                    ? "bg-emerald-500/15 text-emerald-500 border border-emerald-500/30"
                    : "bg-amber-500/15 text-amber-500 border border-amber-500/30"
                }`}
              >
                {submitted ? "submitted" : "draft"}
              </span>
            </div>
            <p className="font-display text-4xl" data-testid="worklog-total-minutes">
              {fmtMinutes(totalMinutes)}
            </p>

            <div className="mt-6 space-y-3">
              {Object.entries(session.categories || {})
                .sort(([, a], [, b]) => b - a)
                .map(([cat, mins]) => {
                  const meta = CATEGORY_META[cat] || { color: "bg-slate-500", label: cat };
                  const Icon = meta.icon || Activity;
                  const pct = totalMinutes > 0 ? (mins / totalMinutes) * 100 : 0;
                  return (
                    <div key={cat} data-testid={`worklog-cat-${cat}`}>
                      <div className="flex items-center gap-2 mb-1 text-sm">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                        <span className="flex-1 capitalize">{meta.label}</span>
                        <span className="tabular-nums text-muted-foreground">{fmtMinutes(mins)}</span>
                        <span className="text-xs text-muted-foreground w-12 text-right">{pct.toFixed(0)}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-secondary/60 overflow-hidden">
                        <div className={`h-full ${meta.color}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
            </div>

            {session.calendar_events && session.calendar_events.length > 0 && (
              <div className="mt-6 pt-4 border-t border-border/40">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Meeting insights</p>
                {(() => {
                  // Group by kind for quick totals.
                  const groups = {};
                  for (const ev of session.calendar_events) {
                    const k = ev.kind || "meeting";
                    if (!groups[k]) groups[k] = { count: 0, minutes: 0 };
                    groups[k].count += 1;
                    groups[k].minutes += (ev.minutes || 0);
                  }
                  const kindMeta = {
                    focus: { label: "Focus time", color: "bg-emerald-500" },
                    sprint: { label: "Sprint ceremonies", color: "bg-sky-500" },
                    review: { label: "Reviews", color: "bg-fuchsia-500" },
                    one_on_one: { label: "1:1s", color: "bg-amber-500" },
                    interview: { label: "Interviews", color: "bg-violet-500" },
                    client: { label: "Client meetings", color: "bg-rose-500" },
                    ooo: { label: "Out of office", color: "bg-slate-500" },
                    meeting: { label: "Other meetings", color: "bg-zinc-500" },
                  };
                  const entries = Object.entries(groups).sort(([, a], [, b]) => b.minutes - a.minutes);
                  return (
                    <div className="flex flex-wrap gap-2 mb-3" data-testid="worklog-meeting-insights">
                      {entries.map(([k, v]) => {
                        const meta = kindMeta[k] || { label: k, color: "bg-slate-500" };
                        return (
                          <div key={k} data-testid={`meeting-insight-${k}`}
                               className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-border/60 text-xs">
                            <span className={`w-2 h-2 rounded-full ${meta.color}`} />
                            <span className="font-medium">{meta.label}</span>
                            <span className="text-muted-foreground tabular-nums">{fmtMinutes(v.minutes)}</span>
                            <span className="text-muted-foreground">· {v.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
                <ul className="text-sm space-y-1">
                  {session.calendar_events.map((ev, i) => (
                    <li key={i} className="flex items-center gap-3 text-muted-foreground" data-testid={`worklog-cal-${i}`}>
                      <span className="font-mono text-[11px] w-24">{ev.start} → {ev.end}</span>
                      <span className="text-foreground flex-1 truncate">{ev.title}</span>
                      <span className="text-[10px] uppercase tracking-wider">{ev.kind}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="border border-border/60 rounded-lg p-6">
            <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Self-report — context your manager will see
            </label>
            <Textarea
              data-testid="worklog-self-report"
              value={selfReport}
              onChange={(e) => setSelfReport(e.target.value)}
              placeholder="What did you actually work on today? Add nuance the categories miss (customer calls, thinking time, blocked-on-X)…"
              rows={6}
              disabled={submitted}
              className="mt-3 bg-background"
            />
            <div className="flex items-center gap-3 mt-4">
              {submitted ? (
                <p className="text-sm text-muted-foreground flex items-center gap-2" data-testid="worklog-locked">
                  <Lock className="w-4 h-4" /> Submitted {new Date(session.submitted_at).toLocaleString()}
                </p>
              ) : (
                <>
                  <Button data-testid="worklog-save-draft-btn" variant="outline" onClick={saveDraft} disabled={saving}>
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Save draft
                  </Button>
                  <Button data-testid="worklog-submit-btn" onClick={submitDay} disabled={submitting}>
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Submit for review
                  </Button>
                  <p className="text-xs text-muted-foreground">Locking the day makes it visible to your manager.</p>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
